<script lang="ts">
  /**
   * The match.
   *
   * Score bar pinned at the top as a broadcast lower-third, commentary as the
   * only scrolling region, and above 1024px a live scorecard pane appears
   * beside it — all straight from the prototype.
   *
   * The feed is newest-first. A chronological feed means the interesting ball
   * is off the bottom of a phone screen the moment it happens.
   */
  import type { Match } from '../lib/match.svelte';
  import { ballNumber, describe, figures } from '../lib/commentary';

  interface Props {
    match: Match;
    onscorecard: () => void;
  }
  let { match, onscorecard }: Props = $props();

  const atBat = $derived(match.batting.filter((b) => b.balls > 0 && !b.out).slice(-2).reverse());
  const usedBowlers = $derived(match.bowling.filter((b) => b.balls > 0));
</script>

<section class="screen active" aria-label="Match">
  <div class="scorebar">
    <div class="scoreline">
      <span class="runs tabular">{match.runs}</span>
      <span class="wk tabular">/{match.wickets}</span>
      <span class="ov tabular">{match.overs}</span>
    </div>
    <div class="teams">
      <span class:you-team={match.youAreBatting} class:opp-team={!match.youAreBatting}>
        {match.battingSideName}
      </span>
      <span class:you-team={!match.youAreBatting} class:opp-team={match.youAreBatting}>
        {match.bowlingSideName}{match.target !== null ? ` ${match.target - 1}` : ''}
      </span>
    </div>

    {#if match.chase}
      <div class="chase tabular">
        {match.chase.runsRequired > 0
          ? `Need ${match.chase.runsRequired} from ${match.chase.ballsRemaining}`
          : 'Target passed'}
      </div>
    {/if}

    <!-- Keyed by index: the values are booleans, and most of them repeat. -->
    <div class="progress" aria-hidden="true">
      {#each match.overProgress as done, i (i)}<i class:done></i>{/each}
    </div>
  </div>

  <div class="two-col">
    <div class="feed" aria-live="polite" aria-label="Ball by ball commentary">
      {#each match.feed as { seq, event } (seq)}
        {@const line = describe(event)}
        <div class="ball">
          <span class="n tabular">{ballNumber(event)}</span>
          <span class="txt">
            {#if line.lead}<span class={line.tone}>{line.lead}</span>{' '}{/if}{line.text}
            {#if line.note}<span class="ms">{line.note}</span>{/if}
          </span>
        </div>
      {/each}

      {#if match.feed.length === 0}
        <p class="faint">Players are taking the field…</p>
      {/if}
    </div>

    <aside class="aside" aria-label="Live scorecard">
      <p class="eyebrow">Batting</p>
      <table class="sc"><tbody>
        {#each atBat as b (b.player.id)}
          <tr>
            <td>{b.player.name} *<span class="how">not out</span></td>
            <td class="n">{b.runs} ({b.balls})</td>
          </tr>
        {/each}
      </tbody></table>

      <p class="eyebrow" style="margin-top:var(--space-4)">Bowling</p>
      <table class="sc"><tbody>
        {#each usedBowlers as b (b.player.id)}
          <tr>
            <td>{b.player.name}</td>
            <td class="n">{figures(b.balls, b.runs, b.wickets)}</td>
          </tr>
        {/each}
      </tbody></table>
    </aside>
  </div>

  <div class="actions">
    <div class="row2">
      <button class="btn-ghost" onclick={() => match.fastForward()}>Skip ahead</button>
      <button class="btn-ghost" onclick={onscorecard}>Scorecard</button>
    </div>
  </div>
</section>
